import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export type ActivityType =
  // Documents
  | "DOCUMENT_CREATED"
  | "DOCUMENT_PUBLISHED"
  | "DOCUMENT_READY_FOR_SIGNING"
  | "DOCUMENT_SIGNED"
  | "DOCUMENT_COMPLETED"
  // Deals
  | "DEAL_CREATED"
  | "DEAL_CLOSED"
  // Events
  | "EVENT_CREATED"
  | "EVENT_UPDATED"
  | "EVENT_COMPLETED"
  // Contacts
  | "CONTACT_CREATED"
  | "CONTACT_UPDATED"
  // Properties
  | "PROPERTY_CREATED"
  | "PROPERTY_UPDATED"
  | "PROPERTY_DATA_UPDATED"
  // Steps
  | "STEP_COMPLETED"
  // Client Needs
  | "CLIENT_NEED_CREATED"
  | "CLIENT_NEED_MATCHED"
  | "CLIENT_NEED_CONVERTED"
  // Property-related
  | "PROPERTY_NOTE_ADDED"
  | "PROPERTY_DOCUMENT_ADDED"
  // Notes
  | "NOTE_ADDED"
  // Document Templates
  | "DOCUMENT_TEMPLATE_CREATED"
  | "DOCUMENT_TEMPLATE_UPDATED"
  // Workflow Templates
  | "WORKFLOW_TEMPLATE_CREATED"
  | "WORKFLOW_TEMPLATE_UPDATED"
  // OCR Batches
  | "OCR_BATCH_CREATED"
  | "OCR_BATCH_DOCUMENT_ADDED"
  | "OCR_BATCH_DOCUMENT_REVIEWED";

export interface Activity {
  id: string;
  type: ActivityType;
  actorId: string;
  actorName: string;
  companyId: string;
  entityType: string;
  entityId: string;
  entityName: string | null;
  parentType: string | null;
  parentId: string | null;
  parentName: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
}

export interface ActivitiesResponse {
  data: Activity[];
  total: number;
}

export interface ActivitiesQueryParams {
  limit?: number;
  offset?: number;
}

export const activitiesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "activitiesApi",
    baseUrl: "/api/activities",
  }),
  reducerPath: "activitiesApi",
  tagTypes: ["TeamActivities", "MyActivities"],
  endpoints: (builder) => ({
    getTeamActivities: builder.query<ActivitiesResponse, ActivitiesQueryParams>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params?.limit !== undefined) queryParams.limit = params.limit.toString();
        if (params?.offset !== undefined) queryParams.offset = params.offset.toString();
        return {
          url: "/team",
          params: queryParams,
        };
      },
      providesTags: ["TeamActivities"],
    }),
    getMyActivities: builder.query<ActivitiesResponse, ActivitiesQueryParams>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params?.limit !== undefined) queryParams.limit = params.limit.toString();
        if (params?.offset !== undefined) queryParams.offset = params.offset.toString();
        return {
          url: "/my",
          params: queryParams,
        };
      },
      providesTags: ["MyActivities"],
    }),
  }),
});

export const { useGetTeamActivitiesQuery, useGetMyActivitiesQuery } = activitiesApi;
